// RevenueCat Web SDK Integration
// This provides a complete mock implementation for development and testing

// RevenueCat configuration
const REVENUECAT_WEB_API_KEY = import.meta.env.VITE_REVENUECAT_WEB_API_KEY;

// Types (simplified versions of what RevenueCat provides)
interface CustomerInfo {
  entitlements: {
    active: { [key: string]: any };
  };
  activeSubscriptions: string[];
  allPurchaseDates: { [key: string]: string };
  firstSeen: string;
  originalAppUserId: string;
  requestDate: string;
}

interface Offering {
  identifier: string;
  serverDescription: string;
  metadata: any;
  availablePackages: Package[];
  monthly?: Package;
  annual?: Package;
}

interface Package {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    description: string;
    title: string;
    price: number;
    priceString: string;
    currencyCode: string;
  };
  offeringIdentifier: string;
}

let isInitialized = false;
let currentCustomerInfo: CustomerInfo | null = null;

// Initialize RevenueCat
export const initializeRevenueCat = async (appUserId?: string): Promise<boolean> => {
  try {
    if (!REVENUECAT_WEB_API_KEY) {
      console.log('RevenueCat running in mock mode - no API key provided');
      isInitialized = true; // Allow mock mode
      return true;
    }

    // NOTE: When using Web Purchase Links, no SDK initialization is needed
    // This mock implementation will be used instead
    
    console.log('RevenueCat mock mode initialized successfully');
    isInitialized = true;
    return true;
  } catch (error) {
    console.error('Failed to initialize RevenueCat:', error);
    return false;
  }
};

// Get customer info
export const getCustomerInfo = async (): Promise<CustomerInfo> => {
  try {
    if (!isInitialized) {
      throw new Error('RevenueCat not initialized. Call initializeRevenueCat() first.');
    }

    // Mock customer info for development
    const mockCustomerInfo: CustomerInfo = {
      entitlements: {
        active: {} // No active entitlements in mock mode
      },
      activeSubscriptions: [],
      allPurchaseDates: {},
      firstSeen: new Date().toISOString(),
      originalAppUserId: 'mock_user',
      requestDate: new Date().toISOString()
    };
    
    currentCustomerInfo = mockCustomerInfo;
    return mockCustomerInfo;
  } catch (error) {
    console.error('Error getting customer info:', error);
    throw error;
  }
};

// Check if user has active subscription
export const hasActiveSubscription = async (entitlementId: string): Promise<boolean> => {
  try {
    const customerInfo = await getCustomerInfo();
    return entitlementId in customerInfo.entitlements.active;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
};

// Get available offerings
export const getOfferings = async () => {
  try {
    if (!isInitialized) {
      throw new Error('RevenueCat not initialized. Call initializeRevenueCat() first.');
    }

    // Mock offerings for development
    const mockOfferings = {
      current: {
        identifier: 'default',
        serverDescription: 'Default offering',
        metadata: {},
        availablePackages: [],
        monthly: null,
        annual: null
      },
      all: {}
    };
    
    return mockOfferings;
  } catch (error) {
    console.error('Error getting offerings:', error);
    throw error;
  }
};

// Make a purchase
export const makePurchase = async (packageOrProductId: any): Promise<CustomerInfo> => {
  try {
    if (!isInitialized) {
      throw new Error('RevenueCat not initialized. Call initializeRevenueCat() first.');
    }
    
    console.log('Mock purchase completed:', packageOrProductId);
    
    // Mock successful purchase
    const mockResult: CustomerInfo = {
      entitlements: {
        active: {
          premium: { 
            identifier: 'premium',
            isActive: true,
            productIdentifier: packageOrProductId.identifier || packageOrProductId
          }
        }
      },
      activeSubscriptions: ['premium'],
      allPurchaseDates: {
        premium: new Date().toISOString()
      },
      firstSeen: new Date().toISOString(),
      originalAppUserId: 'mock_user',
      requestDate: new Date().toISOString()
    };
    
    currentCustomerInfo = mockResult;
    return mockResult;
  } catch (error) {
    console.error('Error making purchase:', error);
    throw error;
  }
};

// Restore purchases
export const restorePurchases = async (): Promise<CustomerInfo> => {
  try {
    if (!isInitialized) {
      throw new Error('RevenueCat not initialized. Call initializeRevenueCat() first.');
    }
    
    console.log('Mock purchases restored');
    
    return await getCustomerInfo();
  } catch (error) {
    console.error('Error restoring purchases:', error);
    throw error;
  }
};

// Set user ID
export const setUserId = async (userId: string): Promise<CustomerInfo> => {
  try {
    if (!isInitialized) {
      throw new Error('RevenueCat not initialized. Call initializeRevenueCat() first.');
    }
    
    console.log('Mock user ID set:', userId);
    
    return await getCustomerInfo();
  } catch (error) {
    console.error('Error setting user ID:', error);
    throw error;
  }
};

// Log out user
export const logoutUser = async (): Promise<CustomerInfo> => {
  try {
    if (!isInitialized) {
      throw new Error('RevenueCat not initialized. Call initializeRevenueCat() first.');
    }
    
    console.log('Mock user logged out');
    
    return await getCustomerInfo();
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

// Get current entitlements
export const getEntitlements = async () => {
  try {
    const customerInfo = await getCustomerInfo();
    return customerInfo.entitlements.active;
  } catch (error) {
    console.error('Error getting entitlements:', error);
    return {};
  }
};

// Check if user is subscribed to specific product
export const isSubscribedToProduct = async (productId: string): Promise<boolean> => {
  try {
    const customerInfo = await getCustomerInfo();
    return customerInfo.activeSubscriptions.includes(productId);
  } catch (error) {
    console.error('Error checking product subscription:', error);
    return false;
  }
};

// Get subscription status
export const getSubscriptionStatus = async () => {
  try {
    const customerInfo = await getCustomerInfo();
    return {
      hasActiveSubscription: customerInfo.activeSubscriptions.length > 0,
      activeSubscriptions: customerInfo.activeSubscriptions,
      entitlements: customerInfo.entitlements.active
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return {
      hasActiveSubscription: false,
      activeSubscriptions: [],
      entitlements: {}
    };
  }
};

export default {
  initializeRevenueCat,
  getCustomerInfo,
  hasActiveSubscription,
  getOfferings,
  makePurchase,
  restorePurchases,
  setUserId,
  logoutUser,
  getEntitlements,
  isSubscribedToProduct,
  getSubscriptionStatus
};